/**
 * ProcedureType eligibility — quyết định 1 `ProcedureType` có thoả với
 * người dùng hiện tại hay không, dùng chung ở:
 *  - `submit-dossier.tsx` (kiểm tra trước khi cho nộp hồ sơ),
 *  - `procedure-list/_components/utils.ts` (lọc filter khi vào danh
 *    sách thủ tục cho khách hàng),
 *  - `sub-header-nav-bar.tsx` (lọc cây loại thủ tục theo cá nhân hoá).
 *
 * Luật khớp phụ thuộc vào `ValueStr`:
 *
 * • **ValueStr có giá trị** (non-empty) — node "có nghiệp vụ":
 *   1. `ValueStr` phải BẮT ĐẦU bằng prefix
 *      `ProcedureTypeStartingCodes.<Role>` ứng với role user
 *      (`Student` / `User` (cán bộ) / `Visitor`), AND
 *   2. Nếu `RegexBuilderJson` có cấu hình ≥ 1 entry ⇒ mã định danh
 *      user (MSSV / Số hiệu CB) phải khớp ≥ 1 entry. Không có cấu
 *      hình ⇒ pass mặc định (chỉ cần thoả điều kiện 1).
 *
 * • **ValueStr rỗng** — node "nhãn nhóm" (vd: 'Sinh viên' cha của
 *   'Chính quy' / 'VLVH' / 'Sau đại học'):
 *   1. Bỏ qua prefix check (rỗng không khớp prefix nào), AND
 *   2. RegexBuilderJson của chính node (nếu có) phải match
 *      identityCode user (không có cấu hình ⇒ pass), AND
 *   3. PHẢI có ≥ 1 con (đệ quy) eligible — nếu không có con (leaf
 *      rỗng) ⇒ luôn fail vì không xác định được nhóm đối tượng.
 *
 * • **Cờ nhóm Sinh viên từ UIS** (`BUSINESS_CONFIG.student_procedure_type_source
 *   = 'uis'`) — với role Student, node `student*` khớp khi
 *   `ValueStr === student_<StudentInfo.StudyTypeID>` (bỏ RegexBuilderJson).
 *   Xem `uis-student-procedure-type.ts`.
 *
 * Không OR giữa regex và prefix. Trước đây submit-dossier.tsx dùng OR
 * như "legacy fallback" — luật mới là AND để tránh prefix đúng nhưng
 * mã user không thuộc nghiệp vụ đang xét (vd: SV Thạc sĩ `C*` không
 * được nộp thủ tục cho ĐH chính quy `K*` dù cả 2 đều bắt đầu bằng
 * `student`).
 */

import { PROCEDURE_TYPE_ALL_VALUE } from '@/components/custom-layout/customer-v2/type';
import { AuthenticateInfo } from '../auth/types';
import { ProcedureTypeStartingCodes } from '@/types/enum';
import {
    HrmStaffCategory,
    IHrmThongTinGiangVienResponse,
    mapMaPhanLoaiToStaffCategory,
} from '../employee/hrm-giang-vien';
import {
    matchesAnyRegexEntry,
    parseRegexBuilderJson,
} from './regex-builder';
import { ProcedureTypeResponse } from './type';
import {
    buildUisStudentToken,
    isUisStudentProcedureTypeEnabled,
} from './uis-student-procedure-type';

/**
 * Role của user dùng để tra prefix `ProcedureTypeStartingCodes`.
 * Khi user chưa xác định được role (chưa đăng nhập hoặc thiếu
 * StudentInfo/StaffInfo/CustomerInfo) ⇒ `null`.
 */
export type EligibleUserRole = {
    /**Tên role — dùng để map sang `ProcedureTypeStartingCodes`. */
    role: 'Student' | 'Staff' | 'Visitor';
    /**Mã định danh thực tế dùng để khớp regex (MSSV / số hiệu CB / ...). */
    identityCode?: string;
    /**Prefix tương ứng — convenience, để caller không phải tra enum lại. */
    startingCode: ProcedureTypeStartingCodes;
    /**
     * Nhóm phân loại cán bộ (suy từ HRM `MaPhanLoai`) — CHỈ có ý nghĩa khi
     * `role === 'Staff'`. `undefined` = chưa xác định được (HRM lỗi / thiếu
     * `CivilServantCode` / user chưa được khai phân loại). Dùng để lọc các node
     * loại thủ tục gate theo phân loại (`employee` / `employee_manager` /
     * `employee_management_board`) — xem [[isProcedureTypeEligibleForUser]].
     *
     * Bơm vào qua [[withStaffCategory]] sau khi fetch
     * `HRM_GetThongTinGiangVien` (hook `useQueryThongTinGiangVien`).
     */
    staffCategory?: HrmStaffCategory;
    /**
     * Mã loại hình đào tạo (`StudentInfo.StudyTypeID`) — CHỈ có ý nghĩa khi
     * `role === 'Student'`. Dùng khi cờ nhóm Sinh viên lấy từ UIS bật: khớp
     * node `student_<StudyTypeID>`. Rỗng ⇒ không khớp node sinh viên nào.
     */
    studyTypeId?: string;
};

/**
 * Suy ra role + identityCode của user hiện tại từ `AuthenticateInfo`.
 * Ưu tiên: Student → Staff → Visitor.
 *
 * - Student: có `StudentInfo.StudentID` → role 'Student', identityCode = MSSV.
 * - Staff:   có `StaffInfo.SoHieuCongChuc` → role 'Staff', identityCode = số hiệu.
 * - Visitor: có `CustomerInfo` (không phải SV/CB) → role 'Visitor',
 *            identityCode rỗng (Visitor không cần regex).
 *
 * Trả `null` khi không xác định được — caller xử lý fallback theo
 * nghiệp vụ (vd: trang public hiện toàn bộ cây).
 */
export const resolveUserRole = (
    authData?: AuthenticateInfo
): EligibleUserRole | null => {
    const extraInfo = authData?.ExtraInfo;
    if (!extraInfo) return null;
    if (extraInfo.StudentInfo?.StudentID) {
        return {
            role: 'Student',
            identityCode: extraInfo.StudentInfo.StudentID,
            startingCode: ProcedureTypeStartingCodes.Student,
            studyTypeId: extraInfo.StudentInfo.StudyTypeID?.trim() || undefined,
        };
    }
    if (extraInfo.StaffInfo?.SoHieuCongChuc) {
        return {
            role: 'Staff',
            identityCode: extraInfo.StaffInfo.SoHieuCongChuc,
            startingCode: ProcedureTypeStartingCodes.User,
        };
    }
    if (extraInfo.CustomerInfo) {
        return {
            role: 'Visitor',
            // Visitor không có mã định danh cố định để khớp regex; regex
            // pattern cho visitor (nếu admin có cấu hình) sẽ luôn không
            // match — trừ khi admin để trống regex để pass-through.
            identityCode: undefined,
            startingCode: ProcedureTypeStartingCodes.Visitor,
        };
    }
    return null;
};

/**
 * Chuẩn hoá kiểm tra prefix — case-insensitive, trim trước khi so sánh
 * để dữ liệu `ValueStr` lỡ có space đầu/cuối cũng pass. Không dùng
 * `.includes()` (luật cũ) vì semantic mới là **bắt đầu bằng**.
 */
const startsWithCode = (
    valueStr: string | null | undefined,
    code: string
): boolean => {
    if (!valueStr) return false;
    return valueStr.trim().toLowerCase().startsWith(code.toLowerCase());
};

/**
 * Danh sách `ValueStr` của các node loại thủ tục "gate theo phân loại cán bộ".
 * Trùng đúng tập giá trị enum [[HrmStaffCategory]] — mọi giá trị đều bắt đầu
 * bằng prefix `employee` nên vẫn qua được `startsWithCode` cho role Staff.
 */
const STAFF_CATEGORY_TOKENS = new Set<string>(
    Object.values(HrmStaffCategory)
);

/**
 * Nhóm "cơ sở" khi chưa xác định được phân loại cán bộ: chỉ node này còn hiển
 * thị, `employee_manager` / `employee_management_board` bị ẩn.
 */
const STAFF_CATEGORY_FALLBACK: HrmStaffCategory = HrmStaffCategory.Employee;

/** `ValueStr` có phải node gate theo phân loại cán bộ không. */
const isStaffCategoryToken = (valueStr: string | null | undefined): boolean =>
    !!valueStr && STAFF_CATEGORY_TOKENS.has(valueStr.trim());

/**
 * So khớp 1 node gate-theo-phân-loại với `staffCategory` của user:
 *  - Đã biết `staffCategory` → khớp CHÍNH XÁC (giống semantic whitelist ở
 *    `submit-dossier.tsx`): quản lý KHÔNG thấy node `employee` thuần và ngược lại.
 *  - Chưa biết (HRM lỗi / thiếu mã) → chỉ cho nhóm cơ sở `employee`.
 */
const staffCategoryMatches = (
    valueStr: string,
    userRole: EligibleUserRole
): boolean => {
    const token = valueStr.trim();
    return userRole.staffCategory
        ? userRole.staffCategory === token
        : token === STAFF_CATEGORY_FALLBACK;
};

/**
 * Bơm `staffCategory` (suy từ HRM `MaPhanLoai`) vào `EligibleUserRole`.
 * No-op nếu role không phải Staff hoặc role null. Truyền `undefined`
 * `thongTinGiangVien` (HRM lỗi / chưa fetch) ⇒ `staffCategory` = undefined
 * ⇒ chỉ thấy nhóm cơ sở `employee`.
 */
export const withStaffCategory = (
    userRole: EligibleUserRole | null,
    thongTinGiangVien?: Pick<IHrmThongTinGiangVienResponse, 'MaPhanLoai'>
): EligibleUserRole | null => {
    if (!userRole || userRole.role !== 'Staff') return userRole;
    return {
        ...userRole,
        staffCategory: mapMaPhanLoaiToStaffCategory(
            thongTinGiangVien?.MaPhanLoai
        ),
    };
};

/** Cây loại thủ tục có node gate-theo-phân-loại cán bộ nào không. */
export const treeHasStaffCategoryNode = <
    T extends Pick<ProcedureTypeResponse, 'ValueStr' | 'Childrens'>,
>(
    tree: T[]
): boolean =>
    tree.some(
        (n) =>
            isStaffCategoryToken(n.ValueStr) ||
            (n.Childrens?.length
                ? treeHasStaffCategoryNode(n.Childrens as unknown as T[])
                : false)
    );

/**
 * Self-check regex của 1 node — KHÔNG quan tâm tới prefix hay con.
 * Pure: trả `true` nếu (không có cấu hình regex) HOẶC (có cấu hình +
 * identityCode match ≥ 1 entry). Trả `false` khi có cấu hình mà user
 * không có identityCode hoặc không match entry nào.
 */
const selfRegexMatches = (
    item: Pick<ProcedureTypeResponse, 'RegexBuilderJson'>,
    userRole: EligibleUserRole
): boolean => {
    const entries = parseRegexBuilderJson(item.RegexBuilderJson);
    if (entries.length === 0) return true;
    if (!userRole.identityCode) return false;
    return matchesAnyRegexEntry(entries, userRole.identityCode);
};

/**
 * 1 `ProcedureType` có thoả với user không?
 *
 * - ValueStr non-empty ⇒ AND(prefix, regex).
 *   • Ngoại lệ: role Staff + ValueStr là node gate theo phân loại cán bộ
 *     (`employee` / `employee_manager` / `employee_management_board`) ⇒
 *     AND(prefix, staffCategory khớp, self regex). `staffCategory` lấy từ HRM
 *     `MaPhanLoai` (bơm qua [[withStaffCategory]]); chưa có ⇒ chỉ node
 *     `employee` pass.
 * - ValueStr rỗng ⇒ bỏ prefix, AND(self regex, ≥1 con đệ quy
 *   eligible). Leaf rỗng (không con) ⇒ false.
 *
 * @param item - ProcedureType cần kiểm tra (cần `Childrens` cho
 *               nhánh ValueStr rỗng; truyền array phẳng vẫn được
 *               nhưng node rỗng sẽ luôn fail).
 * @param userRole - kết quả [[resolveUserRole]]; truyền `null` để báo
 *                   chưa có user → mọi item đều **không** eligible.
 */
export const isProcedureTypeEligibleForUser = (
    item: Pick<
        ProcedureTypeResponse,
        'ValueStr' | 'RegexBuilderJson' | 'Childrens'
    >,
    userRole: EligibleUserRole | null
): boolean => {
    if (!userRole) return false;
    const hasValueStr =
        !!item.ValueStr && item.ValueStr.trim().length > 0;

    if (hasValueStr) {
        // Node có nghiệp vụ: AND(prefix, regex).
        if (!startsWithCode(item.ValueStr, userRole.startingCode))
            return false;

        // Nhóm Sinh viên lấy từ UIS: node `student_<StudyTypeID>` — khớp
        // chính xác StudyTypeID của SV, bỏ qua RegexBuilderJson. Thiếu
        // StudyTypeID ⇒ không khớp (SV chỉ thấy thủ tục dùng chung).
        if (
            userRole.role === 'Student' &&
            isUisStudentProcedureTypeEnabled()
        ) {
            return (
                !!userRole.studyTypeId &&
                item.ValueStr!.trim() ===
                    buildUisStudentToken(userRole.studyTypeId)
            );
        }

        // Cán bộ + node gate theo phân loại (employee / employee_manager /
        // employee_management_board): `SoHieuCongChuc` KHÔNG suy ra được phân
        // loại nên bỏ luật regex, so khớp trực tiếp `staffCategory` (lấy từ HRM
        // `MaPhanLoai`). AND thêm self-regex để admin vẫn siết được nếu muốn.
        if (
            userRole.role === 'Staff' &&
            isStaffCategoryToken(item.ValueStr)
        ) {
            return (
                staffCategoryMatches(item.ValueStr!, userRole) &&
                selfRegexMatches(item, userRole)
            );
        }

        return selfRegexMatches(item, userRole);
    }

    // Node ValueStr rỗng — "nhãn nhóm":
    // (a) Regex của chính node phải match (nếu có cấu hình).
    if (!selfRegexMatches(item, userRole)) return false;

    // (b) ≥ 1 con (đệ quy) eligible. Leaf rỗng ⇒ fail.
    const children = item.Childrens ?? [];
    if (children.length === 0) return false;
    return children.some((c) => isProcedureTypeEligibleForUser(c, userRole));
};

/**
 * Lọc danh sách phẳng các `ProcedureType` theo eligibility.
 * Dùng cho filter ở [[procedure-list/_components/utils.ts]].
 *
 * Lưu ý: với node ValueStr rỗng, luật eligibility cần kiểm tra con —
 * nếu items truyền vào không có `Childrens`, node rỗng sẽ luôn fail.
 * Caller dùng `getAll` (phẳng) thường chỉ assign nghiệp vụ vào leaf
 * non-empty nên trường hợp này chấp nhận được.
 */
export const filterEligibleProcedureTypes = <
    T extends Pick<
        ProcedureTypeResponse,
        'ValueStr' | 'RegexBuilderJson' | 'Childrens'
    >,
>(
    items: T[],
    userRole: EligibleUserRole | null
): T[] => items.filter((x) => isProcedureTypeEligibleForUser(x, userRole));

/**
 * Lọc cây `ProcedureType` (đệ quy):
 *  - Cha eligible HOẶC có ít nhất 1 con (đệ quy) eligible ⇒ giữ.
 *  - Cha không eligible mà không có con eligible ⇒ tỉa.
 *  - Children của cha sau khi lọc = danh sách con đã lọc.
 *
 * Lý do giữ cha không-eligible nếu có con eligible: trong nghiệp vụ,
 * cha thường chỉ là "nhãn nhóm" (vd: "Sinh viên"), `ValueStr` của cha
 * có thể không bắt đầu bằng startingCode nào cụ thể; user chỉ cần thoả
 * con là vẫn thấy được cấu trúc cha-con.
 */
export const filterEligibleProcedureTypeTree = <
    T extends Pick<
        ProcedureTypeResponse,
        'ValueStr' | 'RegexBuilderJson' | 'Childrens'
    >,
>(
    tree: T[],
    userRole: EligibleUserRole | null
): T[] => {
    if (!userRole) return tree; // không có user → trả nguyên cây.

    const result: T[] = [];
    for (const node of tree) {
        const filteredChildren = node.Childrens
            ? filterEligibleProcedureTypeTree(
                node.Childrens as unknown as T[],
                userRole
            )
            : [];
        const selfEligible = isProcedureTypeEligibleForUser(node, userRole);

        if (selfEligible || filteredChildren.length > 0) {
            result.push({
                ...node,
                Childrens: filteredChildren as any,
            });
        }
    }
    return result;
};

/**
 * Tìm **p-types mặc định** cho user trong cây tree.
 *
 * Quy tắc:
 *  - Duyệt root theo Ordinal (API đã sort). KHÔNG dừng ở root không
 *    eligible — vẫn tiếp tục đi sâu vào con vì root có thể là nhãn
 *    nhóm KHÔNG match prefix nhưng vẫn chứa con eligible (PM xác
 *    nhận: cha có con eligible thì coi như eligible cho default).
 *  - Mỗi root: thu thập "anchor ValueStr" từ các CON (đệ quy) — anchor
 *    là node eligible có ValueStr non-empty đầu tiên gặp ở mỗi nhánh
 *    (gặp anchor thì DỪNG nhánh, không đi sâu hơn để tránh duplicate
 *    cha/con).
 *  - Nếu root không có anchor con + chính root eligible + non-empty
 *    ⇒ dùng chính root (case leaf hoặc cây 1 cấp).
 *  - Không root nào trả anchor ⇒ `null`.
 */
export const resolveDefaultPTypesForUser = <
    T extends Pick<
        ProcedureTypeResponse,
        'ValueStr' | 'RegexBuilderJson' | 'Childrens'
    >,
>(
    tree: T[],
    userRole: EligibleUserRole | null
): string | null => {
    if (!userRole || !tree.length) return null;

    /**
     * Đệ quy thu thập "anchor ValueStr" trong subtree. Luật:
     *  - Node eligible + ValueStr non-empty ⇒ push, DỪNG nhánh.
     *  - Node eligible + ValueStr rỗng ⇒ đệ quy con (tìm anchor trong
     *    cháu, vì cha là nhãn nhóm không có anchor riêng).
     *  - Node NOT eligible ⇒ vẫn đệ quy con. Mục đích: cha non-empty
     *    KHÔNG match prefix của user (vd admin set prefix sai) nhưng
     *    có con khớp ⇒ default vẫn nhận anchor con.
     */
    const collectAnchors = (nodes: T[]): string[] => {
        const out: string[] = [];
        for (const n of nodes) {
            const eligible = isProcedureTypeEligibleForUser(n, userRole);
            const hasValueStr = !!n.ValueStr && n.ValueStr.length > 0;

            if (eligible && hasValueStr) {
                out.push(n.ValueStr!);
            } else if (n.Childrens?.length) {
                out.push(...collectAnchors(n.Childrens as unknown as T[]));
            }
        }
        return out;
    };

    for (const root of tree) {
        // Đệ quy con trước — PM ưu tiên tổng hợp anchor của các con.
        const childAnchors = collectAnchors(
            (root.Childrens ?? []) as unknown as T[]
        );
        if (childAnchors.length > 0) return childAnchors.join(',');

        // Không có anchor con → fallback chính root (chỉ khi root
        // self-eligible + non-empty, vd cây 1 cấp leaf).
        if (
            isProcedureTypeEligibleForUser(root, userRole) &&
            root.ValueStr &&
            root.ValueStr.length > 0
        ) {
            return root.ValueStr;
        }
    }

    return null;
};

/**
 * Lấy tập hợp tất cả `ValueStr` của các node trong cây đã filter —
 * dùng để xác nhận một `p-types` URL có hợp lệ với user hay không
 * (case `personalized` cần redirect nếu invalid).
 */
export const collectValueStrsFromTree = <
    T extends Pick<ProcedureTypeResponse, 'ValueStr' | 'Childrens'>,
>(
    tree: T[]
): Set<string> => {
    const set = new Set<string>();
    const walk = (nodes: T[]) => {
        for (const n of nodes) {
            if (n.ValueStr && n.ValueStr.length > 0) {
                set.add(n.ValueStr);
            }
            if (n.Childrens?.length) {
                walk(n.Childrens as unknown as T[]);
            }
        }
    };
    walk(tree);
    return set;
};

/**
 * "Gộp label cha-con" theo nghiệp vụ personalized:
 *  - Đi từ gốc xuống. Nếu một node chỉ còn ĐÚNG 1 con eligible duy
 *    nhất ⇒ merge label cha + con thành 1 chuỗi, đệ quy xuống con của
 *    con. Kết quả là 1 node "phẳng" (`Childrens` rỗng) với label đã
 *    nối + `ValueStr` của leaf cuối cùng được dùng làm anchor.
 *  - Nếu node có ≥ 2 con eligible ⇒ giữ nguyên cấu trúc cha; mỗi con
 *    đệ quy lại cùng quy tắc.
 *  - Nếu node là lá (không con) ⇒ giữ nguyên.
 *
 * Trả về cây đã transform — giữ nguyên shape `ProcedureTypeResponse`
 * để sub-header render bằng component cũ; chỉ `Name` và `Childrens`
 * thay đổi.
 *
 * `nameJoin` (default `' '`): chuỗi nối khi gộp (vd: "Sinh viên Chính
 * quy Loại 1").
 */
export const collapseSingleChildBranches = <T extends ProcedureTypeResponse>(
    tree: T[],
    nameJoin: string = ' '
): T[] => {
    const walk = (node: T): T => {
        const children = (node.Childrens ?? []) as T[];

        // Lá → giữ nguyên.
        if (children.length === 0) return node;

        // Đệ quy con trước — đảm bảo con cũng đã được collapse trước
        // khi quyết định có gộp với cha không.
        const collapsedChildren = children.map(walk);

        // Có nhiều hơn 1 con sau khi collapse → giữ nguyên cha, dùng
        // mảng con đã collapse.
        if (collapsedChildren.length > 1) {
            return { ...node, Childrens: collapsedChildren };
        }

        // Đúng 1 con duy nhất → gộp label cha + con; node mới thừa kế
        // ValueStr từ con (anchor) để link click hoạt động đúng. Giữ
        // ID/RegexBuilderJson... của con để tránh dùng nhầm
        // dữ liệu cha (cha thường là "nhãn nhóm" không có nghiệp vụ
        // cụ thể). Childrens của node con đã được collapse → có thể là
        // mảng rỗng hoặc tiếp tục có nhiều con; tôn trọng kết quả đó.
        const onlyChild = collapsedChildren[0];
        return {
            ...onlyChild,
            Name: [node.Name, onlyChild.Name].filter(Boolean).join(nameJoin),
        } as T;
    };

    return tree.map(walk);
};

/** Chế độ hiển thị/lọc cây loại thủ tục — đọc từ `NEXT_PUBLIC_PROCEDURE_TYPE_MODE`. */
export type ProcedureTypeSelectionMode = 'public' | 'personalized';

export interface ResolvePTypeAutoRedirectArgs {
    /** Cây loại thủ tục thô (IsShowUi) lấy từ API — chưa filter/collapse. */
    rawProcedureTypes: ProcedureTypeResponse[];
    /** Query cây đang tải? Khi đang tải thì CHƯA quyết định được. */
    isTreeLoading: boolean;
    /** Role user (null = chưa đăng nhập / chưa xác định). */
    userRole: EligibleUserRole | null;
    /** p-types hiện tại trên URL ('' nếu chưa có). */
    currentPType: string;
    /** Chế độ cá nhân hoá. */
    mode: ProcedureTypeSelectionMode;
    /** User đã CHỦ ĐỘNG chọn p-type chưa (tôn trọng lựa chọn, không auto). */
    userInteracted: boolean;
}

export interface PTypeAutoRedirectResult {
    /**
     * Có cần auto-redirect p-types không. `false` nghĩa là p-types trên URL
     * đã "ổn định" (settled) → an toàn để load dữ liệu phụ thuộc p-types.
     */
    shouldRedirect: boolean;
    /** Giá trị p-types sẽ ghi vào URL khi `shouldRedirect` = true. */
    target: string | null;
    /** Personalized + user không khớp loại nào (chỉ còn mục "Tất cả"). */
    isPersonalizedNoMatch: boolean;
}

/**
 * SINGLE SOURCE OF TRUTH cho quyết định auto-redirect `p-types` mặc định.
 *
 * Dùng chung bởi:
 *  - `sub-header-nav-bar.tsx` (vùng "Danh sách thủ tục") để THỰC HIỆN redirect.
 *  - `use-procedure-selection-ready.ts` (gate query bảng thủ tục) để biết khi
 *    nào p-types đã ổn định, tránh bảng fetch trước rồi fetch lại lần 2.
 *
 * Hàm thuần — KHÔNG kiểm tra `pathname` (caller tự guard). Khi `shouldRedirect`
 * = false thì p-types hiện tại được coi là đã settled.
 */
export const resolvePTypeAutoRedirect = (
    args: ResolvePTypeAutoRedirectArgs
): PTypeAutoRedirectResult => {
    const {
        rawProcedureTypes,
        isTreeLoading,
        userRole,
        currentPType,
        mode,
        userInteracted,
    } = args;

    const idle: PTypeAutoRedirectResult = {
        shouldRedirect: false,
        target: null,
        isPersonalizedNoMatch: false,
    };

    // Chưa có dữ liệu cây → chưa quyết định được, coi như "đang chờ".
    if (isTreeLoading) return idle;
    // User đã chủ động chọn → tôn trọng, không auto-default.
    if (userInteracted) return idle;

    // Tính personalized-no-match (đồng bộ với sub-header-nav-bar).
    let isPersonalizedNoMatch = false;
    if (mode === 'personalized') {
        const filtered = filterEligibleProcedureTypeTree(
            rawProcedureTypes,
            userRole
        );
        if (userRole && rawProcedureTypes.length > 0 && filtered.length === 0) {
            isPersonalizedNoMatch = true;
        }
    }

    if (isPersonalizedNoMatch) {
        // Đã đúng sentinel "Tất cả" → settled.
        if (currentPType === PROCEDURE_TYPE_ALL_VALUE) {
            return { ...idle, isPersonalizedNoMatch: true };
        }
        return {
            shouldRedirect: true,
            target: PROCEDURE_TYPE_ALL_VALUE,
            isPersonalizedNoMatch: true,
        };
    }

    // Cây dùng để xác định p-types được phép + default.
    const eligibleTree =
        mode === 'personalized'
            ? filterEligibleProcedureTypeTree(rawProcedureTypes, userRole)
            : rawProcedureTypes;
    const allowedValueStrs = collectValueStrsFromTree(
        mode === 'personalized'
            ? collapseSingleChildBranches(eligibleTree)
            : rawProcedureTypes
    );
    const defaultPTypes = resolveDefaultPTypesForUser(eligibleTree, userRole);

    // Không có default (vd khách vãng lai / public không xác định được) →
    // p-types rỗng = "lấy tất cả" là trạng thái cuối → settled.
    if (!defaultPTypes) return idle;

    const currentValues = currentPType
        ? currentPType.split(',').filter(Boolean)
        : [];
    const isAllValid =
        currentValues.length > 0 &&
        currentValues.every((v) => allowedValueStrs.has(v));

    const shouldRedirect =
        (mode === 'public' && !currentPType) ||
        (mode === 'personalized' && (!currentPType || !isAllValid));

    if (!shouldRedirect) return idle;

    if (mode === 'personalized') {
        const defaultValues = defaultPTypes.split(',').filter(Boolean);
        if (
            defaultValues.length === 0 ||
            !defaultValues.every((v) => allowedValueStrs.has(v))
        ) {
            return idle;
        }
    }

    // No-op nếu currentPType và defaultPTypes cùng tập giá trị → settled.
    const currentSet = new Set(currentValues);
    const defaultSet = new Set(defaultPTypes.split(',').filter(Boolean));
    const isSameSet =
        currentSet.size === defaultSet.size &&
        [...currentSet].every((v) => defaultSet.has(v));
    if (isSameSet) return idle;

    return { shouldRedirect: true, target: defaultPTypes, isPersonalizedNoMatch: false };
};
